import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  useGetOccupancyRatesSummaryQuery,
  useGetOccupancyRatesDetailsQuery,
  useLazyGetOccupancyRatesExportDetailsQuery,
} from '@client/api';
import { Card, exportToExcel, Pagination, Typography, useRouteDocumentTitle } from '@client/toolkit';
import { FaCalendarAlt } from 'react-icons/fa';
import { OccupancyRateTable } from './components/occupancy-rate-table';
import { ReportingOccupancyRateCard } from './components/reporting-occupancy-rate-card';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { ReportingPaths } from '../reporting.router';
import dayjs from 'dayjs';
import { SortDirection } from '../components/expanable-table-wraper';
import { OccupancyRateMenu } from './components/menu';
import { transformToExcelFormat } from './utils/transformToExcelFormat';

export function ReportingOccupancyRateRoute() {
  const { t, i18n } = useTranslation('reporting');
  const [searchParams] = useSearchParams();
  useRouteDocumentTitle(t('reporting.documentTitle'), ReportingPaths.OccupancyRate);
  
  const startDateParam = searchParams.get('startDate');
  const endDateParam = searchParams.get('endDate');
  const initialDate = startDateParam ? dayjs(startDateParam).toDate() : dayjs().startOf('year').toDate();
  const initialEndDate = endDateParam ? dayjs(endDateParam).toDate() : dayjs().endOf('year').toDate();
  
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(initialEndDate);
  const [page, setPage] = useState(0);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirection | undefined>(undefined);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  const { data, isFetching } = useGetOccupancyRatesSummaryQuery(
    {
      startDate: dayjs(selectedDate).format('YYYY-MM-DD'),
      endDate: dayjs(selectedEndDate).format('YYYY-MM-DD'),
    },
    {
      skip: !selectedDate || !selectedEndDate,
    },
  );

  const { data: detailsData, isFetching: isFetchingDetails } = useGetOccupancyRatesDetailsQuery(
    {
      startDate: dayjs(selectedDate).format('YYYY-MM-DD'),
      endDate: dayjs(selectedEndDate).format('YYYY-MM-DD'),
      orderBy: sortColumn && sortDirection ? `${sortColumn}:${sortDirection}` : undefined,
      page: page,
      pageSize: 10,
    },
    {
      skip: !selectedDate || !selectedEndDate,
    },
  );

  const [trigger] = useLazyGetOccupancyRatesExportDetailsQuery();
  const onExportExcel = useCallback(async () => {
    setExporting(true);
    const result = await trigger({
      startDate: dayjs(selectedDate).format('YYYY-MM-DD'),
      endDate: dayjs(selectedEndDate).format('YYYY-MM-DD'),
      orderBy: sortColumn && sortDirection ? `${sortColumn}:${sortDirection}` : undefined,
      pageSize: 0,
    });

    if (result.isSuccess) {
      exportToExcel(
        transformToExcelFormat(result.data, selectedDate.getFullYear(), selectedRows),
        'occupancy-rate-report.xlsx',
      );
    }
    setExporting(false);
  }, [selectedDate, selectedEndDate, selectedRows, sortColumn, sortDirection, trigger]);

  return (
    <ReportPageContent
      breadcrumb={t('occupancyRateTitle')}
      menu={
        <OccupancyRateMenu
          selectedDate={selectedDate}
          selectedEndDate={selectedEndDate}
          onChange={(startDate, endDate) => {
            setSelectedDate(startDate);
            setSelectedEndDate(endDate);
          }}
          onExportExcel={onExportExcel}
          exporting={exporting}
        />
      }
    >
      <div className="flex gap-5 justify-between h-[146px] text-white">
        <ReportingOccupancyRateCard
          title={t('occupancyRate', { year: selectedDate.getFullYear() })}
          value={data?.currentYearOccupancyRate ?? 0}
          loading={isFetching}
        />
        <ReportingOccupancyRateCard
          title={t('occupancyRate', { year: selectedDate.getFullYear() - 1 })}
          value={data?.previousYearOccupancyRate ?? 0}
          loading={isFetching}
        />
        <ReportingOccupancyRateCard
          title={t('occupancyRateDevelopment')}
          value={data?.occupancyRateChangePercentage ?? 0}
          showDirection
          loading={isFetching}
        />
      </div>
      <div>
        <span className="flex items-baseline gap-2 mb-4 text-sm">
          <FaCalendarAlt className="text-white" />
          <Typography className="text-white text-sm">
            {t('dateRange', {
              startDate: dayjs(selectedDate).subtract(1, 'year').toDate().toLocaleDateString(i18n.language), // format using locale code
              endDate: selectedDate.toLocaleDateString(i18n.language),
            })}
          </Typography>
        </span>
        <Card className="mb-4">
          <OccupancyRateTable
            data={detailsData?.items ?? []}
            loading={isFetchingDetails}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            baseYear={selectedDate.getFullYear()}
            onSort={(col, direction) => {
              setSortColumn(col);
              setSortDirection(direction);
              setPage(0);
            }}
            onSelected={(ids) => {
              setSelectedRows(ids);
            }}
          />
          <Pagination
            totalCount={detailsData?.total ?? 0}
            currentPage={page}
            setPage={setPage}
            pageSize={detailsData?.pageSize ?? 10}
            visible={true}
          />
        </Card>
      </div>
    </ReportPageContent>
  );
}
